import { Controller } from "@hotwired/stimulus"

const seatColor = name =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

export default class extends Controller {
  static targets = ["seat", "propertiesForm", "emptyMessage", "seatId", "row", "number", "block", "category", "x", "y", "hoverInfo"]
  static values = {
    venueId: String,
    hoverPrompt: String,
    draggingLabel: { type: String, default: "Position" },
    savedMessage: { type: String, default: "Seat updated" },
    saveFailed: { type: String, default: "Failed to save seat" }
  }

  connect() {
    console.log("Seat editor connected")
    this.selectedSeat = null
    this.isDragging = false
    this.draggedSeat = null
    this.dragStarted = false

    // Bind mouse events to document for drag
    this.boundMouseMove = this.drag.bind(this)
    this.boundMouseUp = this.stopDrag.bind(this)
  }

  showHover(event) {
    if (this.isDragging) return

    const seat = event.currentTarget
    const row = seat.dataset.seatRow
    const number = seat.dataset.seatNumber
    const block = seat.dataset.seatBlock
    const category = seat.dataset.seatCategory

    this.hoverInfoTarget.innerHTML = `
      <strong>${category}</strong> | ${block || '—'} | ${row} / ${number} |
      (${Math.round(seat.getAttribute('cx'))}, ${Math.round(seat.getAttribute('cy'))})
    `
  }

  hideHover(event) {
    if (!this.isDragging) {
      this.resetHoverInfo()
    }
  }

  resetHoverInfo() {
    this.hoverInfoTarget.innerHTML = `<strong>${this.hoverPromptValue}</strong>`
  }

  disconnect() {
    document.removeEventListener("mousemove", this.boundMouseMove)
    document.removeEventListener("mouseup", this.boundMouseUp)
  }

  selectSeat(event) {
    // Don't select if we just finished dragging
    if (this.dragStarted) {
      this.dragStarted = false
      return
    }

    const seat = event.currentTarget
    const seatId = seat.dataset.seatId

    // Highlight selected seat
    this.seatTargets.forEach(s => {
      s.setAttribute("stroke", seatColor("--seat-stroke"))
      s.setAttribute("stroke-width", "2")
    })
    seat.setAttribute("stroke", seatColor("--seat-stroke-selected"))
    seat.setAttribute("stroke-width", "3")

    this.selectedSeat = {
      id: seatId,
      element: seat
    }

    // Load seat data from data attributes
    this.loadSeatData(seat)

    // Show form and hide empty message
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.add("hidden")
    }
    this.propertiesFormTarget.classList.remove("hidden")
  }

  loadSeatData(seat) {
    const seatId = seat.dataset.seatId
    const row = seat.dataset.seatRow
    const number = seat.dataset.seatNumber
    const block = seat.dataset.seatBlock
    const category = seat.dataset.seatCategory
    const x = Math.round(parseFloat(seat.getAttribute("cx")))
    const y = Math.round(parseFloat(seat.getAttribute("cy")))

    this.seatIdTarget.value = seatId
    this.rowTarget.value = row
    this.numberTarget.value = number
    this.blockTarget.value = block
    this.categoryTarget.value = category
    this.xTarget.value = x
    this.yTarget.value = y
  }

  updateProperty(event) {
    // Update seat visualization from form inputs
    if (this.selectedSeat) {
      const x = parseFloat(this.xTarget.value)
      const y = parseFloat(this.yTarget.value)

      if (!isNaN(x) && !isNaN(y)) {
        this.selectedSeat.element.setAttribute("cx", x)
        this.selectedSeat.element.setAttribute("cy", y)
      }

      // Update data attributes for hover display
      this.selectedSeat.element.dataset.seatRow = this.rowTarget.value
      this.selectedSeat.element.dataset.seatNumber = this.numberTarget.value
      this.selectedSeat.element.dataset.seatBlock = this.blockTarget.value
      this.selectedSeat.element.dataset.seatCategory = this.categoryTarget.value
    }
  }

  startDrag(event) {
    event.preventDefault()
    event.stopPropagation()

    this.draggedSeat = event.currentTarget
    this.isDragging = false // Will be set to true only after movement threshold

    // Store initial mouse position
    const svg = this.draggedSeat.ownerSVGElement
    const pt = svg.createSVGPoint()
    pt.x = event.clientX
    pt.y = event.clientY
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())

    this.dragStart = {
      x: svgP.x,
      y: svgP.y,
      seatX: parseFloat(this.draggedSeat.getAttribute("cx")),
      seatY: parseFloat(this.draggedSeat.getAttribute("cy"))
    }

    // Highlight dragged seat
    this.originalFill = this.draggedSeat.getAttribute("fill")
    this.draggedSeat.setAttribute("fill", seatColor("--seat-selected"))

    // Get SVG for coordinate conversion
    this.svgElement = svg

    document.addEventListener("mousemove", this.boundMouseMove)
    document.addEventListener("mouseup", this.boundMouseUp)
  }

  drag(event) {
    if (!this.draggedSeat) return

    event.preventDefault()

    // Convert mouse position to SVG coordinates
    const pt = this.svgElement.createSVGPoint()
    pt.x = event.clientX
    pt.y = event.clientY
    const svgP = pt.matrixTransform(this.svgElement.getScreenCTM().inverse())

    // Calculate movement delta
    const deltaX = svgP.x - this.dragStart.x
    const deltaY = svgP.y - this.dragStart.y

    // Set isDragging to true after small movement threshold
    if (!this.isDragging && (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2)) {
      this.isDragging = true
      this.dragStarted = true
    }

    if (this.isDragging) {
      // Update seat position with 5px grid snapping
      const rawX = this.dragStart.seatX + deltaX
      const rawY = this.dragStart.seatY + deltaY

      // Snap to 5px grid
      const newX = Math.round(rawX / 5) * 5
      const newY = Math.round(rawY / 5) * 5

      this.draggedSeat.setAttribute("cx", newX)
      this.draggedSeat.setAttribute("cy", newY)

      // Update hover info
      this.hoverInfoTarget.innerHTML = `
        <strong>${this.draggingLabelValue}</strong> (${newX}, ${newY})
      `

      // Update form if this seat is selected
      if (this.selectedSeat && this.selectedSeat.element === this.draggedSeat) {
        this.xTarget.value = newX
        this.yTarget.value = newY
      }
    }
  }

  stopDrag(event) {
    document.removeEventListener("mousemove", this.boundMouseMove)
    document.removeEventListener("mouseup", this.boundMouseUp)

    if (this.draggedSeat) {
      // Restore original color or keep selected color
      const isSelected = this.selectedSeat && this.selectedSeat.element === this.draggedSeat
      this.draggedSeat.setAttribute("fill", this.originalFill || "var(--color-info)")

      // Auto-save position if we actually dragged
      if (this.isDragging) {
        const seatId = this.draggedSeat.dataset.seatId
        const x = Math.round(parseFloat(this.draggedSeat.getAttribute("cx")))
        const y = Math.round(parseFloat(this.draggedSeat.getAttribute("cy")))

        this.saveSeatPosition(seatId, x, y)

        // Reset hover info
        this.resetHoverInfo()
      }

      this.draggedSeat = null
    }

    this.isDragging = false
  }

  async saveSeatPosition(seatId, x, y) {
    try {
      const response = await fetch(`/backstage/venues/${this.venueIdValue}/seats/${seatId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': Rails.csrfToken()
        },
        body: JSON.stringify({
          seat: { x, y }
        })
      })

      if (response.ok) {
        console.log(`Seat ${seatId} position saved: x=${x}, y=${y}`)
      }
    } catch (error) {
      console.error('Error saving seat position:', error)
      alert(this.saveFailedValue)
    }
  }

  async saveSeat() {
    if (!this.selectedSeat) return

    const seatId = this.seatIdTarget.value
    const seatData = {
      row: this.rowTarget.value,
      number: this.numberTarget.value,
      block: this.blockTarget.value,
      category: this.categoryTarget.value,
      x: parseInt(this.xTarget.value),
      y: parseInt(this.yTarget.value)
    }

    try {
      const response = await fetch(`/backstage/venues/${this.venueIdValue}/seats/${seatId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': Rails.csrfToken()
        },
        body: JSON.stringify({ seat: seatData })
      })

      const data = await response.json()

      if (data.success) {
        // Update data attributes on the element
        this.selectedSeat.element.dataset.seatRow = seatData.row
        this.selectedSeat.element.dataset.seatNumber = seatData.number
        this.selectedSeat.element.dataset.seatBlock = seatData.block
        this.selectedSeat.element.dataset.seatCategory = seatData.category

        // Show success message
        this.hoverInfoTarget.innerHTML = `<strong class="text-success">✓ ${this.savedMessageValue}</strong>`
        setTimeout(() => this.resetHoverInfo(), 2000)
      } else {
        alert(data.errors || this.saveFailedValue)
      }
    } catch (error) {
      console.error('Error saving seat:', error)
      alert(this.saveFailedValue)
    }
  }
}
